import { useState, useMemo } from "react";
import { Trash2, Mail, MailOpen, X, Search, Inbox } from "lucide-react";
import { useAdmin } from "../admin-context";
import DataTable from "../DataTable";

const ContactsPage = () => {
  const { contactSubmissions, markContactRead, deleteContact } = useAdmin();

  const [selected, setSelected]       = useState(null); // open message drawer
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [search, setSearch]           = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = useMemo(() => {
    return contactSubmissions.filter((c) => {
      const matchSearch =
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.subject?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === "all" || c.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [contactSubmissions, search, filterStatus]);

  const unreadCount = contactSubmissions.filter((c) => c.status === "new").length;

  const handleOpen = async (row) => {
    setSelected(row);
    if (row.status === "new") await markContactRead(row.id);
  };

  const handleDelete = async (id) => {
    await deleteContact(id);
    setDeleteConfirmId(null);
    if (selected?.id === id) setSelected(null);
  };

  const columns = [
    {
      key: "name",
      label: "From",
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {row.status === "new" && <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />}
          <div>
            <p className={`text-sm ${row.status === "new" ? "font-bold text-gray-900" : "font-medium text-gray-700"}`}>{row.name}</p>
            <p className="text-xs text-gray-400">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "subject",
      label: "Subject",
      sortable: true,
      render: (val) => <span className="text-sm text-gray-700">{val || <span className="text-gray-400 italic">No subject</span>}</span>,
    },
    {
      key: "page_slug",
      label: "Page",
      render: (val) => val
        ? <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">/{val}</span>
        : <span className="text-gray-300 text-xs">—</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (val) => val === "new"
        ? <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-bold">New</span>
        : <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Read</span>,
    },
    {
      key: "created_at",
      label: "Date",
      sortable: true,
      render: (val) => val ? new Date(val).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            Contact Submissions
            {unreadCount > 0 && (
              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount} new</span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{contactSubmissions.length} total submissions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
          {[["all","All"],["new","New"],["read","Read"]].map(([val, lbl]) => (
            <button
              key={val}
              onClick={() => setFilterStatus(val)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                filterStatus === val ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-4 py-2 w-full border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filtered}
        loading={false}
        searchKeys={[]}
        emptyText="No submissions yet."
        defaultPageSize={15}
        pageSizeOptions={[15, 30, 50]}
        actions={(row) => (
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleOpen(row)}
              className="p-2 hover:bg-indigo-50 rounded-lg text-indigo-400 transition"
              title="View"
            >
              {row.status === "new" ? <Mail size={15} /> : <MailOpen size={15} />}
            </button>
            <button
              onClick={() => setDeleteConfirmId(row.id)}
              className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition"
              title="Delete"
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
      />

      {/* Message Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50 flex-shrink-0">
              <div>
                <p className="font-bold text-gray-900">{selected.name}</p>
                <p className="text-xs text-gray-400">{selected.email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-gray-200 rounded-full transition">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              {selected.subject && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Subject</p>
                  <p className="text-sm text-gray-800">{selected.subject}</p>
                </div>
              )}
              {selected.page_slug && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">From Page</p>
                  <p className="text-sm text-indigo-600 font-medium">/{selected.page_slug}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Message</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
              </div>
              <p className="text-xs text-gray-400">
                {selected.created_at ? new Date(selected.created_at).toLocaleString("en-US", {
                  month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
                }) : ""}
              </p>
            </div>
            <div className="px-5 py-4 border-t bg-gray-50 flex justify-between items-center flex-shrink-0">
              <button
                onClick={() => { setDeleteConfirmId(selected.id); setSelected(null); }}
                className="flex items-center gap-1.5 text-red-400 hover:text-red-600 text-sm font-medium transition"
              >
                <Trash2 size={14} /> Delete
              </button>
              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || '')}`}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition flex items-center gap-1.5"
              >
                <Mail size={14} /> Reply by Email
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h2 className="font-bold text-gray-900 text-lg">Delete submission?</h2>
            <p className="text-sm text-gray-500 mt-1">This cannot be undone.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition text-sm">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition text-sm">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
